import type { ReactNode } from "react";
import { Button } from "./Button";

/* Состояния по E1: пусто — приглашение, а не ошибка; у ошибки — причина,
   одно действие вперёд и судьба данных. */

type Action = { label: string; onClick?: () => void; href?: string };

function ActionButton({ action, primary, tone }: { action: Action; primary?: boolean; tone?: "acc" | "err" | "warn" | "mut" }) {
  const cls = primary ? "" : "border-bd2 text-ink2";
  const inner = (
    <Button variant={primary ? "primary" : "secondary"} className={`text-[12.5px] ${cls}`} onClick={action.onClick} data-tone={tone}>
      {action.label}
    </Button>
  );
  return action.href ? <a href={action.href}>{inner}</a> : inner;
}

export function EmptyState({
  where,
  title,
  body,
  marks = [],
  primary,
  alt,
}: {
  where: string;
  title: string;
  body: ReactNode;
  /** Полоска «шагов»: true — сделано, false — впереди. */
  marks?: boolean[];
  primary?: Action;
  alt?: Action;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-bd bg-bg" data-state="empty">
      <div className="border-b border-bd bg-bg2 px-3.5 py-[9px] font-mono text-[10.5px] uppercase tracking-[.06em] text-mut">{where}</div>
      <div className="flex min-h-[212px] flex-col items-start gap-3 px-[26px] py-[34px]">
        {marks.length > 0 && (
          <div className="flex gap-1" aria-hidden>
            {marks.map((on, i) => (
              <span key={i} className={`h-2 w-[22px] rounded-[2px] border ${on ? "border-accdim bg-accsoft" : "border-dashed border-bd2 bg-transparent"}`} />
            ))}
          </div>
        )}
        <h2 className="m-0 font-mono text-[17px] font-bold leading-[1.25] tracking-[-.01em] text-ink">{title}</h2>
        <p className="m-0 text-[13px] leading-[1.55] text-ink2 text-pretty">{body}</p>
        {(primary || alt) && (
          <div className="mt-auto flex gap-[9px]">
            {primary && <ActionButton action={primary} primary />}
            {alt && <ActionButton action={alt} />}
          </div>
        )}
      </div>
    </section>
  );
}

export type StateTone = "err" | "warn" | "mut";

const toneStyle: Record<StateTone, { fg: string; bd: string; head: string }> = {
  err: { fg: "var(--err)", bd: "var(--err)", head: "var(--errsoft)" },
  warn: { fg: "var(--warn)", bd: "var(--warn)", head: "var(--warnsoft)" },
  mut: { fg: "var(--mut)", bd: "var(--bd)", head: "var(--bg2)" },
};

/** Карточка ошибки / закрытого шага / отказа в доступе (E1). */
export function StateCard({
  code,
  where,
  tone = "mut",
  title,
  body,
  detail,
  primary,
  alt,
}: {
  code: string;
  where: string;
  tone?: StateTone;
  title: string;
  body: ReactNode;
  detail?: string;
  primary?: Action;
  alt?: Action;
}) {
  const t = toneStyle[tone];
  return (
    <section className="overflow-hidden rounded-lg bg-bg" style={{ border: `1px solid ${t.bd}` }} data-state={code.toLowerCase()} role={tone === "err" ? "alert" : undefined}>
      <div className="flex gap-[9px] border-b border-bd px-3.5 py-[9px]" style={{ background: t.head }}>
        <span className="font-mono text-[10.5px] uppercase tracking-[.06em]" style={{ color: t.fg }}>
          {code}
        </span>
        <span className="flex-1" />
        <span className="font-mono text-[10.5px] text-ink2">{where}</span>
      </div>
      <div className="flex min-h-[188px] flex-col gap-[11px] px-[18px] py-5">
        <h2 className="m-0 font-mono text-[16px] font-bold leading-[1.25] tracking-[-.01em] text-ink">{title}</h2>
        <p className="m-0 text-[13px] leading-[1.55] text-ink2">{body}</p>
        {detail && (
          <pre className="m-0 whitespace-pre-wrap rounded-sm border border-bd bg-bg2 px-[11px] py-[9px] font-mono text-[11.5px] leading-[1.5]" style={{ color: t.fg }}>
            {detail}
          </pre>
        )}
        {(primary || alt) && (
          <div className="mt-auto flex gap-[9px] pt-1.5">
            {primary && <ActionButton action={primary} primary />}
            {alt && <ActionButton action={alt} />}
          </div>
        )}
      </div>
    </section>
  );
}

export function NoAccess({ where = "доступ", body, primary, alt }: { where?: string; body: ReactNode; primary?: Action; alt?: Action }) {
  return <StateCard code="403" where={where} tone="mut" title="Эта страница не для вашей роли" body={body} primary={primary} alt={alt} />;
}

export function NotFound({ primary }: { primary?: Action }) {
  return (
    <StateCard
      code="404"
      where="ссылка"
      title="Страница не найдена"
      body="Возможно, материал перенесли при правке курса. Ссылка сохранена в журнале — администратор увидит, что её открывали."
      primary={primary ?? { label: "на мой маршрут", href: "/" }}
    />
  );
}

/** Скелет списка (E1): повторяет будущую раскладку, мигает 1.6 с. */
export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  const widths = [
    [82, 64],
    [68, 78],
    [90, 52],
    [74, 70],
    [60, 66],
  ];
  return (
    <div aria-busy="true" aria-label="загрузка" className="px-3.5">
      {Array.from({ length: rows }).map((_, i) => {
        const [w1, w2] = widths[i % widths.length] ?? [70, 60];
        const style = { animation: "e1pulse 1.6s ease-in-out infinite", animationDelay: `${i * 0.1}s` };
        return (
          <div key={i} className="grid grid-cols-[26px_minmax(0,1.4fr)_minmax(0,1fr)_62px] items-center gap-3 border-b border-bd py-[11px]">
            <span className="size-[26px] rounded-full bg-card2" style={style} />
            <span className="h-2.5 rounded-[3px] bg-card2" style={{ ...style, width: `${w1}%` }} />
            <span className="h-2.5 rounded-[3px] bg-card2" style={{ ...style, width: `${w2}%` }} />
            <span className="h-2.5 rounded-[3px] bg-card2" style={style} />
          </div>
        );
      })}
      <style>{`@keyframes e1pulse{0%,100%{opacity:.35}50%{opacity:.8}}`}</style>
    </div>
  );
}

export type ToastTone = "ok" | "acc" | "err" | "info";

export function Toast({ tone, title, body, action }: { tone: ToastTone; title: string; body: ReactNode; action?: Action }) {
  const fg = `var(--${tone})`;
  return (
    <div
      role="status"
      className="flex items-center gap-3 rounded-md bg-card px-[15px] py-3"
      style={{ border: `1px solid ${tone === "err" ? "var(--err)" : "var(--bd)"}`, borderLeft: `2px solid ${fg}` }}
    >
      <span aria-hidden className="size-[7px] flex-none rounded-full" style={{ background: fg }} />
      <span className="flex-none text-[13px] text-ink">{title}</span>
      <span className="min-w-0 flex-1 text-[12.5px] leading-[1.45] text-ink2">{body}</span>
      {action && (
        <button type="button" onClick={action.onClick} className="flex-none rounded-sm border border-bd2 bg-transparent px-3 py-1.5 font-mono text-[11.5px]" style={{ color: fg }}>
          {action.label}
        </button>
      )}
    </div>
  );
}
